import CSSRules from './CSSRules';
import StyleUtils from '../utils/StyleUtils';

export default class DynStyles {
  constructor(sheetID) {
    this.sheetID = sheetID;
    this.sheetNode = null;
    this.stylesheet = null;
    this.rulesMap = {};
  }

  // Only for testing purposes
  connectStylesheet() {
    this.sheetNode = document.getElementById(this.sheetID);
    this.stylesheet = this.sheetNode.sheet;
  }

  /**
   * Connects stylesheet + set initial rules
   */
  setupStylesheet() {
    // setTimeout used as fix for Safari
    setTimeout(() => {
      this.sheetNode = document.getElementById(this.sheetID);
      this.stylesheet = this.sheetNode.sheet;
      this.addRules();
    }, 0);
  }

  /**
   * If prop given, add only involved css rules. If not, add all rules from CSSRules
   * @param {String} prop CSSRule property
   * @param {String} value CSSRule value
   */
  addRules(prop, value) {
    if (this.stylesheet) {
      if (prop !== undefined) {  // Only add rules concerning certain prop
        if (CSSRules.hasOwnProperty(prop)) {
          let { rules, relatives } = CSSRules[prop];
          for (let rule = 0; rule < rules.length; rule++) {
            let { index, type, selectors } = rules[rule];
            if (selectors.length > 0)
              this.addRule(selectors.join(', '), `${type}: ${value}`, index);
          }
          if (relatives) // Relative colors
            for (let i = 0; i < relatives.length; i++)
              this.addRelativesRules(relatives[i], value);
        }
      } else {  // Add all default rules
        for (let prop in CSSRules) {
          if (CSSRules.hasOwnProperty(prop)) {
            let { value, rules, relatives, tasks } = CSSRules[prop];
            if (tasks === undefined) // Avoid relatives rules
              for (let rule = 0; rule < rules.length; rule++) {
                let { index, type, selectors } = rules[rule];
                if (selectors.length > 0)
                  this.addRule(selectors.join(', '), `${type}: ${value}`, index);
              }
              if (relatives)  // Relative colors
                for (let i = 0; i < relatives.length; i++)
                  this.addRelativesRules(relatives[i], value);
          }
        }
      }
    }
    return this.stylesheet;
  }

  /**
   * Add rules with a value that is relative to another given value
   * @param {String} prop CSSRule property
   * @param {String} value Given value
   */
  addRelativesRules(prop, value) {
    if (CSSRules.hasOwnProperty(prop)) {
      let { rules, tasks } = CSSRules[prop];
      // Process value
      let processedValue = value;
      for (let t = 0; t < tasks.length; t++) {
        // Apply transformations to value
        let { filter, factor } = tasks[t];
        processedValue = StyleUtils[filter](value, factor);
      }
      for (let rule = 0; rule < rules.length; rule++) {
        let { index, type, selectors } = rules[rule];
        if (selectors.length > 0)
          this.addRule(selectors.join(', '), `${type}: ${processedValue}`, index);
      }
    }
  }

  /**
   * Add a new rule to stylesheet
   * @param {String} selector 
   * @param {String} rule
   * @param {Number} index cssRule identifier
   */
  addRule(selector, rule, index) {
    try {
      if ('insertRule' in this.stylesheet)
        this.stylesheet.insertRule(selector + " { " + rule + " } ", 0);
      else if ('addRule' in this.stylesheet)  // IE < 9
        this.stylesheet.addRule(selector, rule, 0);
      this.rulesMap[index] = this.getCssRule(0);
    } catch(e) {}
    return this;
  }

  /**
   * Get cssRule object from stylesheet
   * @param {Number} id cssRule identifier, Examples: "0", "1"
   * @return {CSSStyleRule}
   */
  getCssRule(id) {
    if (this.stylesheet.cssRules)
      return this.stylesheet.cssRules[id];
    else if (this.stylesheet.rules) // IE < 9
      return this.stylesheet.rules[id];
    else
      return null;
  }

  /**
   * Get cssRule identifier of given CSSStyleRule
   * @param {CSSStyleRule} cssRule CSSStyleRule of interest
   * @return {Number} index of cssRule in stylesheet. Return -1 if not found
   */
  getCssRuleIndex(cssRule) {
    let cssRules = [];
    if (this.stylesheet.cssRules)
      cssRules = this.stylesheet.cssRules;
    else if (this.stylesheet.rules) // IE < 9
      cssRules = this.stylesheet.rules;
    for (let rule in cssRules) {
      if (cssRules.hasOwnProperty(rule)) {
        if (cssRules[rule] === cssRule) {
          return rule;
        }
      }
    }
    return -1;
  }

  /**
   * Set a css variable. Delete involved css rules first, then overwrite with new ones
   * @param {String} prop CSSRule property
   * @param {String} value CSSRule value
   */
  setVariable(prop, value) {
    const group = this.getRulesGroup(prop);
    if (group.length === 0)
      return this;
    for (let i = 0; i < group.length; i++) {
      this.deleteRule(group[i]);
    }
    const relatives = this.getRelativesRulesGroup(prop);
    for (let i = 0; i < relatives.length; i++) {
      this.deleteRule(relatives[i]);
    }
    this.addRules(prop, value);
    return this;
  }

  /**
   * Get rule from rulesMap
   * @param {String} index CSSRule identifier
   * @return {CSSStyleRule}
   */
  getRule(index) {
    return this.rulesMap[index] || {};
  }

  /**
   * Get all rules that use given variable (prop)
   * @param {String} prop CSSRule variable
   * @return {Array} array of index's
   */
  getRulesGroup(prop) {
    return CSSRules[prop] ? CSSRules[prop].rules.map((r) => r.index) : [];
  }

  getRelativesRulesGroup(prop) {
    if (!CSSRules[prop])
      return [];
    let relativesGroup = [];
    let relatives = CSSRules[prop].relatives;
    if (relatives)
      for (let i = 0; i < relatives.length; i++) {
        relativesGroup = relativesGroup.concat(this.getRulesGroup(relatives[i]));
      }
    return relativesGroup;
  }

  /**
   * Removes rule from stylesheet
   * @param {String} key rulesMap key
   */
  deleteRule(key) {
    let rule = this.rulesMap[key];
    if (!rule)
      return this;

    let ruleIndex = this.getCssRuleIndex(rule);
    if (this.stylesheet.deleteRule)
      this.stylesheet.deleteRule(ruleIndex);
    else if (this.stylesheet.removeRule)  // IE < 9
      this.stylesheet.removeRule(ruleIndex);
    this.rulesMap[key] = null;
    return this;
  }

  deleteAllRules() {
    const rulesIds = Object.keys(this.rulesMap);
    for (let i = 0; i < rulesIds.length; i++) {
      this.deleteRule(rulesIds[i]);
    }
  }

  /**
   * Get style node ID
   */
  getID() {
    return this.sheetID;
  }

  toString() {
    return 'Dynamic Styles Generator';
  }
}


// WEBPACK FOOTER //
// ./app/js/components/dynamic-styles/DynStyles.js